import { Flex, Icon, Text } from '@chakra-ui/react'
import { Check, User } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import { DateTimeRangeDropdown } from '~/components/shared/DateTimeRangeDropdown'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { SearchableDropdownTextButton } from '~/components/shared/Dropdown/SearchableDropdownTextButton'
import { useEventLogAgentUsers } from '~/queries/eventLogAgentUsers'
import { DateRange } from '~/types/Shared'

const ALL_AGENTS = 'all'

export interface EventLogFiltersProps {
  onDateTimeRangeChange: (dateTimeRange: DateRange) => void
  onAgentChange: (userId: string | undefined) => void
}

const EventLogFilters = ({
  onDateTimeRangeChange,
  onAgentChange,
}: EventLogFiltersProps) => {
  const { t } = useTranslation()
  const agentUsers = useEventLogAgentUsers()
  const [agentSearch, setAgentSearch] = useState('')
  const [selectedAgentId, setSelectedAgentId] = useState<string>(ALL_AGENTS)
  const [dateTimeRange, setDateTimeRange] = useState<DateRange>(
    DateRange.last30Days(),
  )

  const agentItems = useMemo(() => {
    const renderItem = (id: string, label: string) => ({
      value: id,
      // buttonLabel keeps the collapsed button showing plain text; content is
      // the menu row, with a trailing check on the current selection so the
      // active agent is visible when the list is reopened (the shared dropdown
      // tracks its own selection but doesn't surface it in the menu).
      buttonLabel: label,
      content: (
        <MenuListItem
          primaryText={label}
          rightContent={
            id === selectedAgentId ? <Icon as={Check} /> : undefined
          }
        />
      ),
    })

    const term = agentSearch.trim().toLowerCase()
    const matches = agentUsers.filter(
      (user) => !term || (user.full_name ?? '').toLowerCase().includes(term),
    )
    const sorted = [...matches].sort((a, b) =>
      (a.full_name ?? '').localeCompare(b.full_name ?? ''),
    )
    // "All agents" stays first (and unfiltered) so it's always reachable to
    // clear the filter, and remains items[0] for the dropdown's default.
    return [
      renderItem(ALL_AGENTS, t('eventLog.filters.allAgents')),
      ...sorted.map((user) => renderItem(user.id, user.full_name ?? user.id)),
    ]
  }, [agentUsers, agentSearch, selectedAgentId, t])

  const handleAgentSelect = (value: string) => {
    setSelectedAgentId(value)
    onAgentChange(value === ALL_AGENTS ? undefined : value)
  }

  const handleDateTimeRangeChange = (dateTimeRange: DateRange) => {
    setDateTimeRange(dateTimeRange)
    onDateTimeRangeChange(dateTimeRange)
  }

  return (
    <Flex
      width={'100%'}
      height={'4.5rem'}
      padding={'0rem 1.25rem'}
      justifyContent={'space-between'}
      alignItems={'center'}
      alignSelf={'stretch'}
      borderBottom={'2px solid var(--chakra-colors-border-default)'}
      background={'var(--chakra-colors-bg-default);'}
    >
      <Text
        fontSize={'1.25rem'}
        fontWeight={'800'}
        lineHeight={'1.75rem'}
        color={'var(--chakra-colors-fg-default);'}
      >
        {t('eventLog.title')}
      </Text>
      <Flex gap={'0.75rem'}>
        <Flex
          alignItems="center"
          paddingX="0.75rem"
          borderRadius="0.5rem"
          background="var(--chakra-colors-bg-muted)"
          _hover={{ background: 'var(--chakra-colors-bg-emphasized)' }}
        >
          <SearchableDropdownTextButton<string>
            items={agentItems}
            onSelect={handleAgentSelect}
            onSearchChanged={setAgentSearch}
            leftIcon={User}
            variant={TextButtonVariants.Gray_1300}
            placeholder={t('eventLog.filters.searchAgents')}
            menuOptions={{ width: '16rem', maxHeight: '20rem' }}
          />
        </Flex>
        <DateTimeRangeDropdown
          onValueChange={handleDateTimeRangeChange}
          useQuickActionButton={false}
          dateTimeRange={dateTimeRange}
        />
      </Flex>
    </Flex>
  )
}

export default EventLogFilters
